/**
 * Cross-component coordinator preventing a double POST /auth/complete for the
 * same Google OAuth sign-in.
 *
 * Two independent places react to the same Auth0 `isAuthenticated`/`user`
 * state becoming true and can each call /auth/complete:
 *   1. useAuthForm's page-context OAuth-completion effect — runs the
 *      funnel-aware post-signup dispatcher (hero context, tool funnels,
 *      invites, template onboarding). Only handles it when there's no
 *      `onClose` (i.e. a standalone /signup or /login page, not a modal).
 *   2. TokenHandler (index.tsx) — the app-wide generic auth completion,
 *      always mounted.
 *
 * Both are backed by the same shared Auth0Provider context, so both effects
 * fire in the same React commit when a Google popup resolves. React fires
 * child effects before ancestor effects within a commit, and useAuthForm's
 * owning component is always a descendant of TokenHandler, so useAuthForm's
 * claim below is set before TokenHandler's effect body runs its own check —
 * making this a reliable (not just "usually correct") mutex, not a real race.
 *
 * Without this, both requests concurrently hit provision_new_account() on the
 * backend for a brand-new user; the User insert is protected by a unique
 * index, but the personal Org upsert historically was not, producing a
 * duplicate personal Org (and by extension a duplicate Home/root Space) per
 * signup. See docs/Operations/.. duplicate-space investigation.
 */

let handledByAuthForm = false;

/** Called by useAuthForm's page-context OAuth effect before it calls /auth/complete. */
export function claimAuthCompleteForAuthForm(): void {
  handledByAuthForm = true;
}

/** Called by TokenHandler to check whether it should defer entirely to useAuthForm. */
export function isAuthCompleteClaimedByAuthForm(): boolean {
  return handledByAuthForm;
}

/** Reset on logout so a subsequent sign-in within the same SPA session isn't blocked. */
export function resetAuthCompleteCoordinator(): void {
  handledByAuthForm = false;
}
